import { useEffect, useState } from 'react';
import { usePermission } from '../../../auth/AuthContext';
import type { IndexPrerequisite } from '../../../components/index-page/types';
import type { SelectOption } from '../../../components/inputs/Select';
import { personnelApi, personsApi } from '../../human-resources/personnel/personnelApi';
import type { RoutingDesk } from './routingTypes';

/**
 * Where the caller sits for routing — and the index page's banner entry saying so up front.
 *
 * **Routing is a UNIT's act** (10 Oct 2026): a document is routed from the unit of the caller's own
 * post, acknowledged by the unit it went to (or the one employee named), and cancelled by the unit
 * that sent it. No key grants that, so an account linked to no employee with a post can read and
 * do nothing else — and the page says so BEFORE the click, rather than letting the server's
 * refusal be the first the reader hears of it.
 *
 * **ADVISORY, not blocking** — Receiving's Create needs no post (the desk encodes what arrived),
 * so the entry names the gap without disabling anything. Seeded MET, and a failed fetch leaves it
 * met, so a slow feed never puts a banner over a working page.
 */
export function useRoutingDesk(fetch: (signal: AbortSignal) => Promise<RoutingDesk>): {
  desk: RoutingDesk | null;
  prerequisite: IndexPrerequisite;
} {
  const [desk, setDesk] = useState<RoutingDesk | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    fetch(controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setDesk(next);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [fetch]);

  return {
    desk,
    prerequisite: {
      label: 'your employee post',
      // `== null`: a null part is omitted from the wire and arrives undefined.
      met: desk === null || desk.orgUnitId != null,
      to: '/personnel',
      permission: 'personnel.update',
      advisory:
        "Routing is done by a unit's employees — this account is not linked to an employee with a current post, so it can read routings but not send, acknowledge or cancel one.",
    },
  };
}

/**
 * The employees posted to one unit, for the Route form's optional "addressed to" picker.
 *
 * **Through the personnel list and the batch resolve, both on `personnel.read`** — the masked,
 * audited path every employee picker takes. No name rides on a routing response, and a reader
 * without that key gets no list: the field is not drawn rather than drawn empty.
 */
export function useUnitStaff(unitId: string): { options: SelectOption[]; loading: boolean; canRead: boolean } {
  const canRead = usePermission('personnel.read');
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canRead || unitId === '') {
      setOptions([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    personnelApi
      .list({ pageSize: 100, unit: unitId, status: 'active' }, controller.signal)
      .then(async (page) => {
        const names = await personsApi.resolve(
          page.items.map((p) => p.personId).filter((id): id is string => id != null),
          controller.signal,
        );

        if (controller.signal.aborted) return;

        const byPerson = new Map(names.map((n) => [n.personId, n.displayName]));

        setOptions(
          page.items.map((p) => ({
            value: p.id,
            label: (p.personId != null ? byPerson.get(p.personId) : undefined) ?? p.employeeNumber,
            sublabel: p.positionTitle ?? p.employeeNumber,
            keywords: p.employeeNumber,
          })),
        );
      })
      .catch(() => setOptions([]))
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [canRead, unitId]);

  return { options, loading, canRead };
}
